/**
 * ¿Este fallo fue "no llegué" o "llegué y me dijeron que no"?
 *
 * La distinción que importa no es el código HTTP sino **si hubo respuesta**.
 * Sin `response` la petición nunca llegó: se cortó la señal, no hay ruta al
 * servidor, saltó el timeout. Con `response` el servidor escuchó y dio un
 * veredicto, y ese veredicto manda por más feo que sea.
 *
 * Toda la app depende de leer bien esa diferencia, y en cada capa se paga
 * distinto cuando se lee mal:
 *
 * - **Sesión** (`services/apiClient`). Un refresh que no llega **no puede
 *   costar la sesión**. Alguien que pasa una semana en una obra sin señal
 *   volvía y se encontraba deslogueado, con las cargas pendientes varadas y sin
 *   poder volver a entrar hasta recuperar conexión — justo lo contrario de lo
 *   que la app promete.
 * - **Cola de envíos** (`offline/partesOffline`, `lubricantesOffline`,
 *   `combustibleOffline`). Sin respuesta se encola y se reintenta después. Con
 *   respuesta, encolar sería guardar para siempre algo que el servidor ya
 *   rechazó: eso va a la bandeja con su motivo, no a la cola.
 * - **Autoguardado de formularios largos** (check list, orden correctiva,
 *   planilla). Sin respuesta se insiste solo. Con respuesta, insistir da el
 *   mismo rechazo cada diez segundos, gastando batería y datos del teléfono de
 *   alguien que está en la obra y dejando el indicador parpadeando como si algo
 *   estuviera progresando.
 *
 * Vivía copiada en cinco archivos. Eran idénticas, pero cinco copias de un
 * predicado del que dependen la sesión, la cola y el autoguardado son cinco
 * lugares donde una puede corregirse y las otras cuatro no — y esa clase de
 * divergencia no se nota hasta que alguien pierde una carga.
 */

/** True si la petición nunca llegó al servidor. */
export function esFalloDeRed(error) {
  return !error?.response;
}

/**
 * Cuántos reintentos automáticos como máximo, y cada cuánto.
 *
 * Sólo los usan los autoguardados de formularios largos: la cola de envíos
 * tiene su propia cadencia, porque ahí lo que espera está guardado en el
 * teléfono y puede esperar lo que haga falta.
 *
 * El tope existe porque un teléfono que quedó sin señal en un pozo puede pasar
 * horas así, y un reintento cada diez segundos durante horas no recupera nada
 * que el primero no hubiera recuperado — sólo vacía la batería. Después del
 * tope el aviso sigue en pantalla y los datos siguen intactos: cualquier
 * edición nueva vuelve a intentar el guardado en el acto.
 */
export const REINTENTOS_MAX = 5;
export const REINTENTO_MS = 10000;
